import React, { useEffect, useState, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';

// ─── Public API ───────────────────────────────────────────────────────────────

export const GIFT_CELEBRATION_EVENT = 'issac:gift-celebration';

export function triggerGiftCelebration(emoji: string, quantity = 1): void {
  window.dispatchEvent(
    new CustomEvent(GIFT_CELEBRATION_EVENT, {
      detail: { emoji: emoji || '🎁', quantity },
    })
  );
}

// ─── Audio ────────────────────────────────────────────────────────────────────
// AudioContext is ONLY created inside a real user-gesture callback.
// The polling trigger sets a flag; the next click/tap plays the chime.

let _ctx:      AudioContext | null = null;
let _unlocked  = false;
let _pending   = false;               // chime queued, waiting for next gesture

function _chime(): void {
  if (!_ctx || !_unlocked) return;
  const ctx = _ctx;
  const note = (hz: number, t: number, dur: number, vol = 0.30) => {
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.connect(g); g.connect(ctx.destination);
    o.type = 'sine'; o.frequency.value = hz;
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(vol, t + 0.025);
    g.gain.exponentialRampToValueAtTime(0.001, t + dur);
    o.start(t); o.stop(t + dur + 0.08);
  };
  const t = ctx.currentTime;
  note(523,  t,        0.80);
  note(659,  t + 0.12, 0.75);
  note(784,  t + 0.24, 0.75);
  note(1047, t + 0.36, 1.00);
  note(1319, t + 0.52, 0.65, 0.18);
}

// Called on trusted user gestures. It creates/resumes audio only when a chime
// is actually queued, which avoids Chrome autoplay warnings during normal page use.
function _onGesture(event: Event): void {
  if (!event.isTrusted || (!_pending && !_ctx)) return;

  if (!_ctx) {
    try {
      const Ctor = (window as any).AudioContext || (window as any).webkitAudioContext;
      if (Ctor) _ctx = new Ctor() as AudioContext;
      else return;
    } catch { return; }
  }
  if (_ctx.state === 'running') {
    _unlocked = true;
    if (_pending) { _pending = false; _chime(); }
  } else if (_pending && _ctx.state === 'suspended') {
    _ctx.resume().then(() => {
      _unlocked = true;
      if (_pending) { _pending = false; _chime(); }
    }).catch(() => {});
  }
}

function requestChime(): void {
  if (_unlocked && _ctx?.state === 'running') {
    _chime();
  } else {
    _pending = true;   // play on next gesture
  }
}

// ─── Particles ────────────────────────────────────────────────────────────────

interface Particle {
  id:       number;
  emoji:    string;
  sx:       number;     // start x offset from screen centre (px)
  tx:       number;     // destination x from screen centre (px)
  ty:       number;     // destination y from screen centre (px) — always upward
  fontSize: number;
  halfSize: number;
  scale:    number;
  rotate:   number;
  delay:    number;     // 0 – 0.5 s
  dur:      number;     // individual flight duration
}

const STAGGER    = 0.50;  // max launch delay
const DISMISS_MS = 3_800; // overlay lifetime

function buildParticles(emoji: string, count: number): Particle[] {
  const W    = typeof window !== 'undefined' ? window.innerWidth  : 390;
  const H    = typeof window !== 'undefined' ? window.innerHeight : 844;
  const vmin = Math.min(W, H);

  // Small elegant glyphs: 4–5 vmin, floor 20 px, ceiling 36 px
  const baseFont = Math.max(20, Math.min(vmin * 0.048, 36));

  return Array.from({ length: count }, (_, i) => {
    const fontSize = baseFont * (0.7 + Math.random() * 0.6);

    // Start slightly spread around centre so they don't all overlap
    const sx = (Math.random() - 0.5) * W * 0.25;

    // Drift left/right as they rise — wider spread higher up
    const tx = (Math.random() - 0.5) * W * 0.55;

    // All float upward: between 35% and 90% of screen height
    const ty = -(H * (0.35 + Math.random() * 0.55));

    return {
      id:       i,
      emoji,
      sx,
      tx,
      ty,
      fontSize,
      halfSize: fontSize / 2,
      scale:    0.6 + Math.random() * 0.8,
      rotate:   (Math.random() - 0.5) * 120,  // gentle, not full-spin
      delay:    Math.random() * STAGGER,
      dur:      2.2 + Math.random() * 1.2,
    };
  });
}

function getCount(qty: number): number {
  const W    = typeof window !== 'undefined' ? window.innerWidth : 390;
  const base = W >= 1280 ? 55 : W >= 768 ? 42 : 32;
  return Math.min(base + Math.round((qty - 1) * 5), 75);
}

// ─── Component ────────────────────────────────────────────────────────────────

export const GiftCelebrationOverlay: React.FC = () => {
  const [batch, setBatch] =
    useState<{ particles: Particle[]; runId: number } | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Wire up gesture listeners — creates AudioContext on first interaction
  useEffect(() => {
    const evs = ['click', 'touchstart', 'keydown', 'mousedown'] as const;
    evs.forEach((ev) =>
      document.addEventListener(ev, _onGesture, { passive: true })
    );
    return () =>
      evs.forEach((ev) => document.removeEventListener(ev, _onGesture));
  }, []);

  const handleEvent = useCallback((e: Event) => {
    const { emoji, quantity } =
      (e as CustomEvent<{ emoji: string; quantity: number }>).detail;

    if (timerRef.current) clearTimeout(timerRef.current);

    requestChime();

    setBatch({
      particles: buildParticles(String(emoji || '🎁'), getCount(Number(quantity) || 1)),
      runId:     Date.now(),
    });

    timerRef.current = setTimeout(() => setBatch(null), DISMISS_MS);
  }, []);

  useEffect(() => {
    window.addEventListener(GIFT_CELEBRATION_EVENT, handleEvent);
    return () => {
      window.removeEventListener(GIFT_CELEBRATION_EVENT, handleEvent);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [handleEvent]);

  if (!batch) return null;

  return (
    <div
      key={batch.runId}
      aria-hidden="true"
      style={{
        position:      'fixed',
        inset:         0,
        overflow:      'hidden',
        pointerEvents: 'none',
        zIndex:        99999,
      }}
    >
      {batch.particles.map((p) => (
        <motion.span
          key={`${batch.runId}-${p.id}`}
          style={{
            position:   'absolute',
            left:       '50%',
            top:        '60%',           // start from lower-centre for upward float
            marginLeft: -p.halfSize,
            marginTop:  -p.halfSize,
            fontSize:   p.fontSize,
            lineHeight: 1,
            userSelect: 'none',
            display:    'inline-block',
            willChange: 'transform, opacity',
            filter:     'drop-shadow(0 0 4px rgba(255,100,180,0.55))',
          }}
          initial={{ opacity: 0, x: p.sx, y: 0, scale: 0, rotate: 0 }}
          animate={{
            opacity: [0, 1, 1, 1, 0],
            x:       [p.sx, p.tx * 0.4, p.tx],   // gentle S-curve drift
            y:       p.ty,
            scale:   [0, p.scale * 1.15, p.scale, p.scale * 0.85],
            rotate:  p.rotate,
          }}
          transition={{
            opacity: {
              duration: p.dur,
              delay:    p.delay,
              times:    [0, 0.10, 0.30, 0.72, 1.0],
              ease:     'linear',
            },
            x: {
              duration: p.dur,
              delay:    p.delay,
              ease:     'easeInOut',
              times:    [0, 0.35, 1],
            },
            y: {
              duration: p.dur,
              delay:    p.delay,
              ease:     [0.20, 0.80, 0.40, 1],
            },
            scale: {
              duration: p.dur * 0.35,
              delay:    p.delay,
              times:    [0, 0.20, 0.65, 1],
              ease:     'backOut',
            },
            rotate: {
              duration: p.dur,
              delay:    p.delay,
              ease:     'easeInOut',
            },
          }}
        >
          {p.emoji}
        </motion.span>
      ))}
    </div>
  );
};
